import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { StorageManager } from '../common/storage-manager.class';
import { ClienteModel } from '../models/cliente.model';
import { DashboardService } from './events/dashboard.service';

/** Centralises the selected client state and keeps session storage and dashboard events in sync. */
@Injectable({
    providedIn: 'root',
})
export class ClientSelectionService {
    private readonly dashboardService = inject(DashboardService);

    private readonly selectedClientSubject = new BehaviorSubject<ClienteModel | null>(StorageManager.getClient());

    public readonly selectedClient$: Observable<ClienteModel | null> = this.selectedClientSubject.asObservable();

    public getSelectedClient(): ClienteModel | null {
        return this.selectedClientSubject.value;
    }

    public setSelectedClient(client: ClienteModel | null, emitDashboardChange = true): boolean {
        if (!StorageManager.saveClient(client)) {
            return false;
        }

        const storedClient = StorageManager.getClient();

        this.selectedClientSubject.next(storedClient);

        if (emitDashboardChange) {
            this.dashboardService.emitClientChange();
        }

        return true;
    }

    public syncFromStorage(): ClienteModel | null {
        const selectedClient = StorageManager.getClient();

        this.selectedClientSubject.next(selectedClient);

        return selectedClient;
    }

    public getClientId(client: ClienteModel | string | number | null | undefined): string | null {
        if (client === null || client === undefined) {
            return null;
        }

        const rawClientId = typeof client === 'object' ? this.resolveObjectClientId(client) : client;
        const clientId = String(rawClientId ?? '').trim();

        return clientId.length > 0 ? clientId : null;
    }

    public sameClient(
        left: ClienteModel | string | number | null | undefined,
        right: ClienteModel | string | number | null | undefined
    ): boolean {
        const leftId = this.getClientId(left);
        const rightId = this.getClientId(right);

        return leftId !== null && rightId !== null && leftId === rightId;
    }

    private resolveObjectClientId(client: ClienteModel): string | number | null | undefined {
        return client.id ?? client.id_cliente;
    }
}
